import {
  AppShellTrigger,
  Box,
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbList,
  BreadcrumbPage,
  Button,
  Icon,
  Text,
  useIsMobile,
} from "@salestrak/ui";
import * as React from "react";

import { SettingsMobileHeaderContext } from "#app/components/navigation/settings-mobile-header";
import { noop } from "#app/lib/utils/noop";
import type { Omittable } from "@salestrak/ui";

const SLOT_NAMES = {
  breadcrumb: "PageHeaderBreadcrumb",
  back: "PageHeaderBack",
  title: "PageHeaderTitle",
  subtitle: "PageHeaderSubtitle",
  actions: "PageHeaderActions",
} as const;

type BackDescriptor = { label: string; onClick: () => void };

function isBackDescriptor(value: unknown): value is BackDescriptor {
  return (
    typeof value === "object" &&
    value !== null &&
    !React.isValidElement(value) &&
    "label" in value &&
    "onClick" in value
  );
}

function findSlot(
  children: React.ReactNode,
  name: string,
): React.ReactElement | null {
  let found: React.ReactElement | null = null;
  React.Children.forEach(children, (child) => {
    if (found || !React.isValidElement(child)) return;
    if ((child.type as { displayName?: string })?.displayName === name) {
      found = child;
    }
  });
  return found;
}

function centeredWrap(content: React.ReactNode) {
  return (
    <Box direction="column" align="center" paddingX="lg">
      <Box fill="width" maxWidth={800}>
        {content}
      </Box>
    </Box>
  );
}

type PageHeaderProps = Omittable<{
  children?: React.ReactNode;
  /** On desktop, center the header content within a max-width container to align with page content. */
  centered?: boolean;
  /** String wraps in single-segment breadcrumb chrome; JSX renders as-is (pass `<PageHeaderTitle>` for big titles, `<Breadcrumb>` for multi-segment trails). */
  title?: string | React.ReactElement;
  /** String wraps in `<PageHeaderSubtitle>`; JSX renders as-is. */
  subtitle?: string | React.ReactElement;
  /** `{label,onClick}` wraps in `<PageHeaderBack>`; JSX renders as-is. */
  back?: BackDescriptor | React.ReactElement;
  /** Wraps in `<PageHeaderActions>` unless already a `<PageHeaderActions>` element. */
  actions?: React.ReactNode;
}>;

export function PageHeader({
  children,
  centered = false,
  title,
  subtitle,
  back,
  actions,
}: PageHeaderProps) {
  const isMobile = useIsMobile();
  const hasSettingsMobileHeader = React.useContext(SettingsMobileHeaderContext);

  const synthesized: React.ReactNode[] = [];

  if (title != null) {
    synthesized.push(
      typeof title === "string" ? (
        <PageHeaderBreadcrumb>
          <BreadcrumbItem>
            {/* Pass the raw string — BreadcrumbPage carries the page-title
                type (base + semibold) and truncates. Nesting another
                (default-size) Text inside it clips ascenders: the outer
                truncate's overflow-hidden cuts the taller inner line box. */}
            <BreadcrumbPage>{title}</BreadcrumbPage>
          </BreadcrumbItem>
        </PageHeaderBreadcrumb>
      ) : (
        title
      ),
    );
  }

  if (subtitle != null) {
    synthesized.push(
      typeof subtitle === "string" ? (
        <PageHeaderSubtitle>{subtitle}</PageHeaderSubtitle>
      ) : (
        subtitle
      ),
    );
  }

  if (back != null) {
    synthesized.push(
      isBackDescriptor(back) ? (
        <PageHeaderBack label={back.label} onClick={back.onClick} />
      ) : (
        back
      ),
    );
  }

  if (actions != null) {
    const isAlreadyWrapped =
      React.isValidElement(actions) &&
      (actions.type as { displayName?: string })?.displayName ===
        SLOT_NAMES.actions;
    synthesized.push(
      isAlreadyWrapped ? actions : <PageHeaderActions>{actions}</PageHeaderActions>,
    );
  }

  const allChildren: React.ReactNode =
    synthesized.length > 0
      ? [...synthesized, ...React.Children.toArray(children)]
      : children;

  const breadcrumbEl = findSlot(allChildren, SLOT_NAMES.breadcrumb);
  const backEl = findSlot(allChildren, SLOT_NAMES.back);
  const titleEl = findSlot(allChildren, SLOT_NAMES.title);
  const subtitleEl = findSlot(allChildren, SLOT_NAMES.subtitle);
  const actionsEl = findSlot(allChildren, SLOT_NAMES.actions);

  const hasBack = !!backEl;
  const shouldCenter = centered && !isMobile;
  const stackTitle =
    (!isMobile && (hasBack || shouldCenter)) ||
    (isMobile && hasBack && hasSettingsMobileHeader);

  const navLeft =
    breadcrumbEl ??
    backEl ??
    (shouldCenter ? <InvisibleBackSpacer /> : null);

  const navRow = (
    <Box
      direction="row"
      align="center"
      justify={!stackTitle && actionsEl ? "between" : "start"}
      className={shouldCenter && stackTitle ? "ml-6" : undefined}
    >
      <Box direction="row" align="center" gap="sm" shrink>
        {navLeft}
        {!stackTitle && titleEl}
      </Box>
      {!stackTitle && actionsEl}
    </Box>
  );

  const titleRow =
    stackTitle && titleEl ? (
      <Box
        direction="row"
        align="center"
        justify={actionsEl ? "between" : "start"}
        gap="sm"
      >
        {titleEl}
        {actionsEl}
      </Box>
    ) : null;

  const subtitleSlot = subtitleEl
    ? shouldCenter
      ? centeredWrap(subtitleEl)
      : subtitleEl
    : null;

  return (
    <Box
      direction="column"
      // Uniform inset (matches paddingX) so the title/actions sit equidistant
      // from all edges of the content card — `md` desktop, `sm` mobile.
      paddingY={isMobile ? "sm" : "md"}
      paddingX={shouldCenter ? undefined : isMobile ? "sm" : "md"}
      gap={stackTitle ? "xs" : undefined}
    >
      {shouldCenter && !stackTitle ? centeredWrap(navRow) : navRow}
      {titleRow ? (shouldCenter ? centeredWrap(titleRow) : titleRow) : null}
      {subtitleSlot}
    </Box>
  );
}
PageHeader.displayName = "PageHeader";

function InvisibleBackSpacer() {
  return (
    <Box pointerEvents="none" opacity={0}>
      <PageHeaderBack label="Back" onClick={noop} />
    </Box>
  );
}

type PageHeaderBreadcrumbProps = {
  /** Breadcrumb segments — must contain `BreadcrumbItem`/`BreadcrumbSeparator` children. For a single label, use `<PageHeader title="...">` instead. */
  children: React.ReactNode;
};

export function PageHeaderBreadcrumb({ children }: PageHeaderBreadcrumbProps) {
  // On mobile the site header rides inside the nav drawer, so the page
  // breadcrumb carries the trigger that opens it. Settings screens render
  // theirs in `SettingsMobileHeader` instead — suppress the duplicate.
  const isMobile = useIsMobile();
  const hasSettingsMobileHeader = React.useContext(SettingsMobileHeaderContext);
  const showMenu = isMobile && !hasSettingsMobileHeader;
  return (
    <Box direction="row" align="center" gap="sm" shrink>
      {showMenu && <AppShellTrigger />}
      <Breadcrumb>
        <BreadcrumbList>{children}</BreadcrumbList>
      </Breadcrumb>
    </Box>
  );
}
PageHeaderBreadcrumb.displayName = SLOT_NAMES.breadcrumb;

type PageHeaderBackProps = {
  label: string;
  onClick: () => void;
};

export function PageHeaderBack({ label, onClick }: PageHeaderBackProps) {
  return (
    // `-ml-2.5` cancels the sm size's own horizontal padding so the chevron
    // aligns flush with the container edge.
    <Button variant="ghost" size="sm" className="-ml-2.5" onClick={onClick}>
      <Icon name="chevron-left" color="muted" />
      <Text variant="muted">{label}</Text>
    </Button>
  );
}
PageHeaderBack.displayName = SLOT_NAMES.back;

type PageHeaderTitleProps = Omittable<{
  children: React.ReactNode;
  /** Inline content rendered next to the title (e.g. icon, badge). */
  adornment?: React.ReactNode;
  /** Controls the title text size. Default renders at 2xl, "sm" renders at lg for breadcrumb-style headers. */
  size?: "default" | "sm";
}>;

export function PageHeaderTitle({
  children,
  adornment,
  size = "default",
}: PageHeaderTitleProps) {
  const isMobile = useIsMobile();
  const responsiveSize = isMobile ? "sm" : size === "sm" ? "lg" : "2xl";
  return (
    <Box direction="row" align="center" gap="sm" shrink>
      {/* The page's principal heading — h1 so it carries the heading role
          screen readers and tests navigate by, not a bare span. */}
      <Text as="h1" size={responsiveSize} weight="semibold" truncate>
        {children}
      </Text>
      {adornment}
    </Box>
  );
}
PageHeaderTitle.displayName = SLOT_NAMES.title;

export function PageHeaderSubtitle({
  children,
}: {
  children: React.ReactNode;
}) {
  return (
    <Text size="sm" variant="muted">
      {children}
    </Text>
  );
}
PageHeaderSubtitle.displayName = SLOT_NAMES.subtitle;

export function PageHeaderActions({
  children,
}: {
  children: React.ReactNode;
}) {
  return (
    <Box direction="row" align="center" gap="xs">
      {children}
    </Box>
  );
}
PageHeaderActions.displayName = SLOT_NAMES.actions;
